import { CircularProgress, Grid, makeStyles, Theme } from '@material-ui/core';
import { capitalizeAll, ChartSettings, convertCollectionDataToMetricsDataPoints, getLanguageByCode, mergeMetricDataToMultiMetricDataPoints,
    MultiSeriesDataPoint, GenericMessageBox, TimeSeriesChartPanel, TimeSeriesChartPanelProps, TimeSeriesType, XYChart, XYChartType,
    TimeSeriesValueType, DataPoint } from '@moderepo/mode-app-components';
import { ritzDataContext, selectLoggedInAgent } from '@moderepo/mode-data-state-ritz';
import { fetchTimeSeriesCollectionData, fetchTimeSeriesCollectionRawData, selectTimeSeriesCollectionData, selectTimeSeriesCollectionRawData,
    userAppDataContext } from '@moderepo/mode-data-state-user-app';
import { Agent, Entity, EntityMetricsDefinition, NumericMetricInfo, TagMetricInfo, TimeSeriesAggregation } from '@moderepo/mode-internal-apis';
import { modeUIContext, selectLanguage } from '@moderepo/mode-ui-state';
import moment from 'moment';
import React, { CSSProperties, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { filterMetricsByMetricsExclusionCustomSettings, GridSizeSettings, MetricsExclusionCustomSettings, useSelectTranslatedEntityClasses }
    from '..';
import { FetchDataByTimeRangeOptions, LazyComp } from '../..';
import { DEFAULT_LANGUAGE, BasicXYChart, SUPPORTED_LANGUAGES } from '../../../utils/constants';
import { useRitzTranslation } from '../../../utils/customHooks';
import { useSelectEntity, useSelectProject } from '../../../utils/dataStateHooks';
import { getPreferredLanguage, sortObjectAttributes } from '../../../utils/utils';
import { getNumericMetricsRange } from '../../robot-cloud/RCUtils';
import { RouteHelper } from '../../routes/RouteHelper';
import { GanttChart } from '../../time-series/GanttChart';
import { getMetricsDefinitionMetricsId } from '../utils';


const useStyle = makeStyles((theme: Theme) => {
    return {
        gridItem: {
            '& .chart-container': {
                height    : 250,
                '& .chart': {
                    height: '100%',
                },
            },

            [theme.breakpoints.up('md')]: {
                '& .chart-container': {
                    height: 275,
                },
            },

            [theme.breakpoints.up('lg')]: {
                '& .chart-container': {
                    height: 300,
                },
            },
        },

        noDataMessage: {
            display       : 'flex',
            flexDirection : 'column',
            alignItems    : 'center',
            justifyContent: 'center',
            flex          : 1,
        },
    };
}, { name: 'MetricDefinitionViewContainer', index: 1 });


// How many RAW data point we want to show for each chart. This value will be used if the container does not provide a value.
const DEFAULT_RAW_DATA_COUNT = 200;



// Temporary Interface for storing params required for fetching TSDB aggregated data
interface FetchTSDBAggregatedDataParams {
    readonly homeId: number;
    readonly moduleId: string;
    readonly collectionId: string,
    readonly startTime: number;
    readonly endTime: number;
    readonly valueNames: string[];
    readonly aggregation?: TimeSeriesAggregation | undefined;
}


// Temporary Interface for storing params required for fetching TSDB raw data
interface FetchTSDBRawDataParams {
    readonly homeId: number;
    readonly moduleId: string;
    readonly collectionId: string,
    readonly timestamp: number;
    readonly valueNames: string[];
    readonly tagNames: string[];
    readonly limit: number;
}



/**
 * Fetch all Entity metrics data belonged to the specified metricsDefinition
 */
const useFetchData = (
    projectId: number,
    entity: Entity | undefined,
    metricsDefinition: EntityMetricsDefinition | undefined,
    startTime: number,
    endTime: number,
    showValueType: TimeSeriesValueType | undefined,
    defaultAggregation: TimeSeriesAggregation | undefined,
    dataCount: number,
    metricsExclusionSettings: MetricsExclusionCustomSettings | undefined,
): {
    numericMetrics: readonly MultiSeriesDataPoint<number>[] | undefined,
    tagMetrics: readonly MultiSeriesDataPoint<string>[] | undefined,
} | undefined => {

    const { state:appDataState, dispatch:appDataDispatch } = useContext(userAppDataContext);

    // The state to keep track of the params needed for fetching missing data
    const [paramsForFetchingMissingAggregatedData, setParamsForFetchingMissingAggregatedData] = useState<FetchTSDBAggregatedDataParams[]>();
    const [paramsForFetchingMissingRawData, setParamsForFetchingMissingRawData] = useState<FetchTSDBRawDataParams[]>();
    const hasErrors = useRef<boolean>(false);


    const result = useMemo(() => {
        if (!hasErrors.current && entity && metricsDefinition) {

            // Create a new array of params for fetching missing data.
            const newParamsForFetchingMissingAggregatedData: FetchTSDBAggregatedDataParams [] = [];
            const newParamsForFetchingMissingRawData: FetchTSDBRawDataParams [] = [];

            const smartModuleId = metricsDefinition.dataStoreConfiguration.moduleInstanceId;
            const collectionId = getMetricsDefinitionMetricsId(entity, metricsDefinition.metricsDefinitionId);

            if (showValueType === TimeSeriesValueType.AGGREGATED) {

                // Get the list of metrics from the metricDefinition that we need to fetch data for. We don't need to fetch data for
                // ALL metrics. We only need to fetch data for the metrics based on the "metricsExclusionSettings".
                // Also, because different metrics use different aggregations, we can't make 1 API call to fetch data for all metrics. We need to
                // group the metrics by aggregation and then for each group, we make 1 API call.
                const numericMetricsGroupedByAggregation = filterMetricsByMetricsExclusionCustomSettings(
                    metricsDefinition.metricsDefinitionId,
                    metricsDefinition.numericMetrics,
                    metricsExclusionSettings,
                    true,
                ).reduce((result: {
                    readonly [aggregation: string]: NumericMetricInfo[]
                }, metricInfo) => {
                    // Get the metricInfo's aggregation. If the metricInfo is a KPI metric, it should have the aggregation in "evaluator".
                    // Else, use the provided "defaultAggregation" in the param. If not provided, use the AVG as the default.
                    const aggregation = metricInfo.evaluator?.aggregation ?? defaultAggregation ?? TimeSeriesAggregation.AVG;

                    const metrics = result[aggregation] ?? [];
                    metrics.push(metricInfo);
                    return {
                        ...result,
                        [aggregation]: metrics,
                    };
                }, {});


                const combinedData: {
                    numericMetrics: {
                        readonly [metricName: string]: DataPoint<number>[];
                    };
                    tagMetrics: {
                        readonly [metricName: string]: DataPoint<string>[];
                    };
                } = {
                    numericMetrics: {},
                    tagMetrics    : {},
                };


                // For each group in numericMetricsGroupedByAggregation, make 1 API call to fetch data for the metrics in that group.
                // eslint-disable-next-line no-restricted-syntax
                for (const [aggregation, numericMetrics] of Object.entries(numericMetricsGroupedByAggregation)) {

                    const valueNames = numericMetrics.map((metric) => {
                        return metric.name;
                    });

                    // Get cached data from the data state
                    const cachedData = selectTimeSeriesCollectionData(
                        appDataState,
                        entity.homeId,
                        smartModuleId,
                        collectionId,
                        moment(startTime).toISOString(),
                        moment(endTime).toISOString(),
                        valueNames,
                        aggregation as TimeSeriesAggregation,
                    );

                    if (cachedData) {
                        const reformattedCollectionData = convertCollectionDataToMetricsDataPoints(cachedData, valueNames, undefined);
                        combinedData.numericMetrics = {
                            ...combinedData.numericMetrics,
                            ...reformattedCollectionData.numericMetrics,
                        };
                    } else {
                        // If we can't find data from cache, call setParamsForFetchingMissingAggregatedData to remember the params required for
                        // fetching the data. The useEffect below will take care of fetching the missing data.
                        newParamsForFetchingMissingAggregatedData.push({
                            homeId     : entity.homeId,
                            moduleId   : smartModuleId,
                            collectionId,
                            startTime,
                            endTime,
                            valueNames,
                            aggregation: aggregation as TimeSeriesAggregation,
                        });
                    }

                    // Stop if there is missing data. Will try again when the data is loaded
                    if (newParamsForFetchingMissingAggregatedData.length > 0) {
                        break;
                    }
                }

                // If we have all the data then return the result.
                if (newParamsForFetchingMissingAggregatedData.length === 0) {
                    return {
                        numericMetrics: mergeMetricDataToMultiMetricDataPoints(combinedData.numericMetrics),
                        tagMetrics    : undefined,
                    };
                }

                // Set the newParamsForFetchingMissingAggregatedData to remember that we need to fetch these missing data. But we only set it
                // if the newParamsForFetchingMissingAggregatedData is different from the current paramsForFetchingMissingAggregatedData
                setParamsForFetchingMissingAggregatedData((currentParams) => {
                    if (
                        !currentParams
                        || JSON.stringify(sortObjectAttributes(currentParams))
                        !== JSON.stringify(sortObjectAttributes(newParamsForFetchingMissingAggregatedData))
                    ) {
                        return newParamsForFetchingMissingAggregatedData;
                    }
                    return currentParams;
                });
            } else {

                // Get the list of valueNames and tagNames from the metricDefinition that we need to fetch data for. We don't need to fetch data for
                // ALL metrics. We only need to fetch data for the metrics based on the "metricsExclusionSettings".
                const numericMetrics = filterMetricsByMetricsExclusionCustomSettings(
                    metricsDefinition.metricsDefinitionId,
                    metricsDefinition.numericMetrics,
                    metricsExclusionSettings,
                    true,
                ).map((metricInfo) => {
                    return metricInfo;
                });

                const tagMetrics = filterMetricsByMetricsExclusionCustomSettings(
                    metricsDefinition.metricsDefinitionId,
                    metricsDefinition.tagMetrics,
                    metricsExclusionSettings,
                    false,
                ).map((metricInfo) => {
                    return metricInfo;
                });

                const valueNames = numericMetrics.map((metric) => {
                    return metric.name;
                });

                const tagNames = tagMetrics.map((metric) => {
                    return metric.name;
                });


                // Get cached data from the data state
                const cachedData = selectTimeSeriesCollectionRawData(
                    appDataState,
                    entity.homeId,
                    smartModuleId,
                    collectionId,
                    moment(startTime).toISOString(),
                    dataCount,
                    valueNames,
                    tagNames,
                );

                if (cachedData) {
                    // We have all the data, we can return the result now.
                    const reformattedCollectionData = convertCollectionDataToMetricsDataPoints(cachedData, valueNames, tagNames);
                    return {
                        numericMetrics: mergeMetricDataToMultiMetricDataPoints(reformattedCollectionData.numericMetrics),
                        tagMetrics    : mergeMetricDataToMultiMetricDataPoints(reformattedCollectionData.tagMetrics),
                    };
                }

                // If we can't find data from cache, call setParamsForFetchingMissingAggregatedData to remember the params required for fetching the
                // data. The useEffect below will take care of fetching the missing data.
                newParamsForFetchingMissingRawData.push({
                    homeId   : entity.homeId,
                    moduleId : smartModuleId,
                    collectionId,
                    limit    : dataCount,
                    timestamp: startTime,
                    valueNames,
                    tagNames,
                });

                // Set the newParamsForFetchingMissingRawData to remember that we need to fetch these missing data. But we only set it if the
                // newParamsForFetchingMissingRawData is different from the current paramsForFetchingMissingRawData
                setParamsForFetchingMissingRawData((currentParams) => {
                    if (
                        !currentParams
                    || JSON.stringify(sortObjectAttributes(currentParams))
                        !== JSON.stringify(sortObjectAttributes(newParamsForFetchingMissingRawData))
                    ) {
                        return newParamsForFetchingMissingRawData;
                    }
                    return currentParams;
                });
            }

        }

        return undefined;
    }, [appDataState, dataCount, defaultAggregation, endTime, entity, metricsDefinition, metricsExclusionSettings, showValueType, startTime]);


    /**
     * This useEffect is used for checking the list of "paramsForFetchingMissingData" to see if there are any actions need to be dispatched
     * and dispatch them.
     */
    useEffect(() => {
        if (paramsForFetchingMissingAggregatedData && paramsForFetchingMissingAggregatedData.length > 0) {
            paramsForFetchingMissingAggregatedData.forEach((params) => {
                appDataDispatch(fetchTimeSeriesCollectionData(
                    params.homeId,
                    params.moduleId,
                    params.collectionId,
                    moment(params.startTime).toISOString(),
                    moment(params.endTime).toISOString(),
                    params.valueNames,
                    params.aggregation,
                )).catch(() => {
                    hasErrors.current = true;
                });
            });
        }
        if (paramsForFetchingMissingRawData && paramsForFetchingMissingRawData.length > 0) {
            paramsForFetchingMissingRawData.forEach((params) => {
                appDataDispatch(fetchTimeSeriesCollectionRawData(
                    params.homeId,
                    params.moduleId,
                    params.collectionId,
                    moment(params.timestamp).toISOString(),
                    params.limit,
                    params.valueNames,
                    params.tagNames,
                )).catch(() => {
                    hasErrors.current = true;
                });
            });
        }
    }, [appDataDispatch, paramsForFetchingMissingAggregatedData, paramsForFetchingMissingRawData]);


    return result;
};



export interface MetricDefinitionViewContainerProps {
    readonly className?: string | undefined;
    readonly style?: CSSProperties | undefined;
    readonly projectId: number;
    readonly entityId: string;
    readonly metricsDefinitionId: string;
    readonly fetchDataOptions: FetchDataByTimeRangeOptions;
    readonly chartIdPrefix: string;

    // The type of metric value to show, "Aggregated" or "Raw". The default is "Raw". If this is set to "Aggregated", only NUMERIC metrics are shown
    readonly showValueType?: TimeSeriesValueType | undefined;

    // Custom settings from entityClass uiSettings
    readonly defaultAggregation?: TimeSeriesAggregation | undefined;

    // Custom settings from entityClass uiSettings
    readonly dataCount?: number | undefined;

    // To include/exclude metrics we want or don't want to show
    readonly metricsExclusionSettings?: MetricsExclusionCustomSettings | undefined;

    readonly showMetricsDefinitionName?: boolean | undefined;
    readonly showLatestValue?: boolean | undefined;
    readonly applyValueRange?: boolean | undefined;
    readonly chartType?: BasicXYChart | undefined;
    readonly gridSizes?: GridSizeSettings | undefined;
}



/**
 * This component display data for 1 entity's metricDefinition
 */
export const MetricDefinitionViewContainer: React.FC<MetricDefinitionViewContainerProps> = (
    props: MetricDefinitionViewContainerProps,
) => {

    const { state:uiState } = useContext(modeUIContext);
    const { state:dataState } = useContext(ritzDataContext);

    const project = useSelectProject(props.projectId);
    const loggedInAgent = selectLoggedInAgent(dataState) as Agent;

    // Get current selected language in the uiState
    const selectedLanguage = getLanguageByCode(
        SUPPORTED_LANGUAGES, selectLanguage(uiState),
    ) || DEFAULT_LANGUAGE;

    // Get the agent's preferred language and figure out the language that is being used in the UI based on the agent's preferred language and the
    // current language in the uiState. If the agent is logged in and has preferred language set than use the agent's preferred language.
    // Else use the current selected language in the UI state.
    const currentLanguage = loggedInAgent
        ? getPreferredLanguage(project, loggedInAgent, DEFAULT_LANGUAGE)
        : selectedLanguage;

    const { trans } = useRitzTranslation(project);
    const classes = useStyle();
    const navigate = useNavigate();
    const routeHelper = RouteHelper.getInstance(navigate);

    const selectedEntity = useSelectEntity(props.projectId, props.entityId, false);
    const entityClasses = useSelectTranslatedEntityClasses(props.projectId, trans);


    // Find the entityClass object that the selectedEntity belonged to
    const selectedEntityClass = useMemo(() => {
        if (selectedEntity && entityClasses) {
            return entityClasses.find((entityClass) => {
                return entityClass.entityClass === selectedEntity.entityClass;
            });
        }
        return undefined;
    }, [entityClasses, selectedEntity]);


    // The selectedEntityClass has multiple metricDefinitions. We need to get the single metricDefinition specified by props.metricDefinitionId
    const selectedMetricsDefinition = useMemo(() => {
        if (selectedEntityClass) {
            return selectedEntityClass.metricsDefinitions?.find((metricDef) => {
                return metricDef.metricsDefinitionId === props.metricsDefinitionId;
            });
        }
        return undefined;
    }, [props.metricsDefinitionId, selectedEntityClass]);



    // The list of metrics that we will display. These are based on the list of metricDefinition's numericMetrics and tagMetrics
    const chartableMetrics = useMemo(() => {
        if (selectedMetricsDefinition) {
            return {
                numericMetrics: filterMetricsByMetricsExclusionCustomSettings(
                    selectedMetricsDefinition.metricsDefinitionId,
                    selectedMetricsDefinition.numericMetrics,
                    props.metricsExclusionSettings,
                    true,
                ).map((metricInfo) => {
                    return [metricInfo];
                }),
                tagMetrics: filterMetricsByMetricsExclusionCustomSettings(
                    selectedMetricsDefinition.metricsDefinitionId,
                    selectedMetricsDefinition.tagMetrics,
                    props.metricsExclusionSettings,
                    false,
                ).map((metricInfo) => {
                    return [metricInfo];
                }),
            };
        }
        return undefined;
    }, [selectedMetricsDefinition, props.metricsExclusionSettings]);


    /**
     * Fetch data for all metrics
     */
    const allMetricsData = useFetchData(
        props.projectId,
        selectedEntity,
        selectedMetricsDefinition,
        props.fetchDataOptions.startTime,
        props.fetchDataOptions.endTime,
        props.showValueType,
        props.defaultAggregation,
        props.dataCount ?? DEFAULT_RAW_DATA_COUNT,
        props.metricsExclusionSettings,
    );



    /**
     * Create the chart settings and use useMemo to cache it
     */
    const chartSettings = useMemo((): ChartSettings => {
        return {
            className: 'chart',
            locale   : currentLanguage.locale2,
        };
    }, [currentLanguage.locale2]);


    /**
     * On view historical data button clicked, take the user to the historical data page
     */
    const onViewHistoricalData = useCallback(() => {
        if (selectedEntity && selectedMetricsDefinition) {
            // Get the collectionId for the given metricDefinitionId
            const collectionId = getMetricsDefinitionMetricsId(selectedEntity, props.metricsDefinitionId);

            routeHelper.gotoHomeHistoricalDataPage({
                projectId        : props.projectId,
                homeId           : selectedEntity.homeId,
                tsdbSmartModuleId: selectedMetricsDefinition.dataStoreConfiguration.moduleInstanceId,
                dataId           : collectionId,
                timeSeriesType   : TimeSeriesType.COLLECTION,
                metricsDefinition: {
                    entityClass        : selectedEntity.entityClass,
                    metricsDefinitionId: selectedMetricsDefinition.metricsDefinitionId,
                },
            });
        }
    }, [props.metricsDefinitionId, props.projectId, routeHelper, selectedEntity, selectedMetricsDefinition]);


    /**
     * Create a set of action to show in each chart panel.
     */
    const chartPanelActions = useMemo((): TimeSeriesChartPanelProps['customSecondaryActions'] => {
        return [{
            icon   : 'show_chart',
            label  : capitalizeAll(trans('label.view_historical_data')),
            onClick: onViewHistoricalData,
        }];
    }, [trans, onViewHistoricalData]);



    // Create and cache texts settings for chart panel
    const chartPanelTextsSettings = useMemo(() => {
        if (props.showLatestValue) {
            return {
                currentValueLabel: capitalizeAll(trans('label.latest_value')),
            };
        }
        return undefined;
    }, [props.showLatestValue, trans]);


    return (
        <>
            {project && selectedEntity && selectedMetricsDefinition && (
                <>
                    {(chartableMetrics?.numericMetrics || []).map((numericMetrics: readonly NumericMetricInfo[]) => {

                        const numericMetric = numericMetrics[0];

                        // Get the latest value from the multiMetricsDataPoints
                        const numericMetricsData = allMetricsData?.numericMetrics;
                        const latestValue = numericMetricsData && numericMetricsData.length > 0
                            ? numericMetricsData[numericMetricsData.length - 1].values[numericMetric.name]
                            : undefined;
                        const yAxesRange = props.applyValueRange ? getNumericMetricsRange(numericMetrics) : undefined;

                        return (
                            <Grid
                                item
                                key={`${props.metricsDefinitionId}-${numericMetric.name}`}
                                className={classes.gridItem}
                                xs={props.gridSizes?.xs ?? 12}
                                sm={props.gridSizes?.sm}
                                md={props.gridSizes?.md}
                                lg={props.gridSizes?.lg}
                                xl={props.gridSizes?.xl}
                            >
                                <TimeSeriesChartPanel
                                    className="chart-container"
                                    title={numericMetric.displayName || numericMetric.name}
                                    subtitle={props.showMetricsDefinitionName
                                        ? selectedMetricsDefinition.displayName ?? selectedMetricsDefinition.metricsDefinitionId : undefined}
                                    dataCount={numericMetricsData?.length || 0}
                                    seriesCount={numericMetrics.length}
                                    customSecondaryActions={chartPanelActions}
                                    texts={chartPanelTextsSettings}
                                    currentValue={props.showLatestValue && latestValue !== undefined
                                        ? trans('label.value_unit_display', {
                                            value: trans('{{value, number-format}}', { value: latestValue }),
                                            unit : numericMetric.unit,
                                        })
                                        : undefined}
                                    noDataMessageComp={(
                                        <GenericMessageBox
                                            className={classes.noDataMessage}
                                            title={trans('generic.no_data.title', {
                                                term: capitalizeAll(trans('label.time_series_data', { count: 2 })),
                                            })}
                                        />
                                    )}
                                >
                                    <LazyComp>
                                        <XYChart
                                            {...chartSettings}
                                            chartType={props.chartType || XYChartType.LINE}
                                            showBullets={false}
                                            showYAxes={numericMetrics.length <= 2}
                                            chartId={
                                                `${props.chartIdPrefix}-MetricDefinitionViewContainer
                                                -${props.metricsDefinitionId}-${numericMetric.name}`
                                            }
                                            dataPoints={numericMetricsData || []}
                                            displayMetrics={numericMetrics}
                                            yAxesRange={yAxesRange}
                                        />
                                    </LazyComp>
                                </TimeSeriesChartPanel>
                            </Grid>
                        );
                    })}

                    {(chartableMetrics?.tagMetrics || []).map((tagMetrics: readonly TagMetricInfo[]) => {

                        const tagMetric = tagMetrics[0];

                        // Get the latest value from the multiMetricsDataPoints
                        const tagMetricsData = allMetricsData?.tagMetrics;
                        const latestValue = tagMetricsData && tagMetricsData.length > 0
                            ? tagMetricsData[tagMetricsData.length - 1].values[tagMetric.name]
                            : undefined;

                        return (
                            <Grid
                                item
                                key={`${props.metricsDefinitionId}-${tagMetric.name}`}
                                className={classes.gridItem}
                                xs={props.gridSizes?.xs ?? 12}
                                sm={props.gridSizes?.sm}
                                md={props.gridSizes?.md}
                                lg={props.gridSizes?.lg}
                                xl={props.gridSizes?.xl}
                            >
                                <TimeSeriesChartPanel
                                    className="chart-container"
                                    title={tagMetric.displayName || tagMetric.name}
                                    subtitle={props.showMetricsDefinitionName ? selectedMetricsDefinition.metricsDefinitionId : undefined}
                                    dataCount={tagMetricsData?.length ?? 0}
                                    seriesCount={tagMetrics.length}
                                    customSecondaryActions={chartPanelActions}
                                    noDataMessageComp={(
                                        <GenericMessageBox
                                            className={classes.noDataMessage}
                                            title={trans('generic.no_data.title', {
                                                term: capitalizeAll(trans('label.time_series_data', { count: 2 })),
                                            })}
                                        />
                                    )}
                                    texts={chartPanelTextsSettings}
                                    currentValue={props.showLatestValue && latestValue !== undefined ? latestValue ?? '' : undefined}
                                >
                                    <LazyComp>
                                        <GanttChart
                                            {...chartSettings}
                                            chartId={
                                                `${props.chartIdPrefix}-MetricDefinitionViewContainer-${props.metricsDefinitionId}-${tagMetric.name}`
                                            }
                                            dataPoints={tagMetricsData ?? []}
                                            displayMetrics={tagMetrics}
                                            dateRange={{
                                                start: 0, end: 0,
                                            }}
                                        />
                                    </LazyComp>
                                </TimeSeriesChartPanel>
                            </Grid>
                        );
                    })}
                </>
            )}

            {!(project && selectedEntity && selectedMetricsDefinition) && (
                <CircularProgress />
            )}
        </>
    );
};
